import type { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { useOwnerSession } from './hooks/useOwnerSession.js'

/**
 * Auth gate for owner pages. Checks the owner session via GET
 * /api/owner/auth/me:
 * - loading → lightweight placeholder
 * - resolved null (401) → redirect to /login
 * - resolved session → render children
 * - errored (500, network) → surface the error with a retry; do NOT bounce
 *   to login (a 500 on /me is a real problem and a silent redirect hides it).
 *
 * Mirrors admin's AdminRoute.
 */
export function OwnerRoute({ children }: { children: ReactNode }) {
  const { data, isLoading, isError, error, refetch } = useOwnerSession()

  if (isLoading) {
    return (
      <div className="owner-root">
        <div className="owner-session-loading">
          <span
            className="stamp-spinner-square stamp-spinner-square--lg"
            aria-hidden="true"
          />
          <p>Checking your session…</p>
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="owner-root">
        <div className="owner-error-state">
          <p>We couldn't verify your session.</p>
          <p className="owner-hint">
            {error instanceof Error ? error.message : 'Unexpected error'}
          </p>
          <button
            type="button"
            className="owner-btn"
            onClick={() => void refetch()}
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  if (!data) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}
